import {
  Box,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import {
  sl1Drift,
  sl1ProviderState,
  sl1Remaining,
  sl1WindowScoped,
} from "shared/sl1ImportData"
import { sl1WindowLabel } from "./sl1WindowLabel"
import { Sl1ImportFleetDto } from "src/features/things/sl1ImportApi"
import {
  Sl1DriftMarker,
  Sl1ProviderStateChip,
  fmtNum,
  fmtStamp,
  panelSx,
  sl1ProviderStateColor,
} from "./Sl1ImportChrome"

/**
 * One row per webapp provider — the lower half of Screen 1 (LOCKER-382 §D-4).
 *
 * Its own file so the overview page stays under the ~400-line component budget
 * (`.claude/rules/frontend-conventions.md`), and because this table is where the
 * derivations live: every state, remainder and drift figure below comes from
 * `shared/sl1ImportData.ts` rather than being recomputed in the browser. Two
 * implementations of `sl1ProviderState` would eventually disagree, and the visible
 * symptom would be a row whose chip contradicts the tiles counting it.
 *
 * Column order is the mockup's, and it is an argument: identity → the mapping,
 * which gates everything → the four counts → progress → when it last moved → what
 * to do next. The operator reads left to right and lands on the action.
 */

/** Severity stripe on the leading cell. A row-level `border-left` is not painted
 *  reliably under `border-collapse`, and this cue must not silently vanish. */
const stripeSx = (color: string) => ({
  borderLeft: "3px solid",
  borderLeftColor: color,
})

/**
 * Pins the Status column to the right edge of the scroll container.
 *
 * `bgcolor` is mandatory, not cosmetic: a sticky cell with a transparent ground
 * lets the scrolling columns render straight through it, which looks like a
 * rendering fault rather than a pinned column. `background.paper` matches the
 * table's own surface in BOTH themes, which a literal white would not.
 *
 * The left border is the seam. Without it there is no visual cue that the
 * content underneath is sliding, and the pinned column reads as clipped.
 */
const stickyStatusSx = {
  position: "sticky" as const,
  right: 0,
  zIndex: 2,
  bgcolor: "background.paper",
  borderLeft: "1px solid",
  borderLeftColor: "divider",
}

export const Sl1ImportFleetTable: FC<{ fleet: Sl1ImportFleetDto[] }> = ({
  fleet,
}) => {
  const { t } = useTranslation()

  // `label`, not `key` — gitleaks' `generic-api-key` rule fires on
  // `key: "<10+ chars>"` and its allowlist cannot be made to work on the version
  // CI pins (see `.gitleaks.toml`). Renaming removes the false positive at source.
  const headers: { label: string; align: "left" | "right" }[] = [
    { label: "sl1ColProvider", align: "left" },
    { label: "sl1ColMapping", align: "left" },
    { label: "sl1ColSl1Rows", align: "right" },
    { label: "sl1ColInSl2", align: "right" },
    { label: "sl1ColImported", align: "right" },
    { label: "sl1ColRemaining", align: "right" },
    { label: "sl1ColProgress", align: "left" },
    // NO separate drift column. The mockup renders drift INLINE with the SL1-rows
    // figure ("14 207 +31 since recon"), which is also what makes the two
    // timestamp columns wide enough to read: a dedicated drift column cost ~110px
    // and squeezed "last recon" and "last import" into illegible stubs once
    // Status was pinned (round-1 F1.7). Inline is both narrower and more
    // faithful — drift is a qualifier on that number, not a fact of its own.
    // ONE "last activity" column, not two timestamps.
    //
    // Eleven columns never fit the ~1140px a 1440px desktop leaves after the
    // sidebar. Pinning Status kept the ACTION visible but pushed the cost onto
    // the two date columns, which rendered as `20:` under a header clipped to
    // `LA` (round-1 F1.7) — present in the DOM, useless on the screen.
    //
    // Both facts survive: the cell shows the most recent touch with a marker for
    // WHICH kind it was, and the other date is on hover. What an operator asks of
    // this column is "when was this provider last touched, and was that a dry run
    // or a write" — which one line answers.
    { label: "sl1ColLastActivity", align: "left" },
    { label: "sl1ColStatus", align: "left" },
  ]

  return (
    // Scrolls inside its own container. Eleven columns do not fit a laptop, and a
    // horizontally-scrolling page BODY puts the identity column out of reach.
    <Box sx={{ ...panelSx, overflowX: "auto" }}>
      {/*
        STATUS IS PINNED TO THE RIGHT EDGE, and that is the fix rather than a
        narrower table.

        Eleven columns do not fit the ~1140px a 1440px desktop leaves once the
        admin sidebar takes its 220px — and trimming them one at a time (shorter
        ids, dropped sub-lines, a smaller minWidth) bought pixels without ever
        buying enough. Status is the column the whole screen builds towards: the
        counts describe a provider, Status says what to DO about it. A reader who
        has to scroll horizontally to reach the verdict will read the numbers and
        never see it.

        So the last column sticks. Every other column scrolls under it, the action
        is on screen at any width, and no information is dropped to get there.

        Worth recording HOW this was found: every assertion in the spec passed
        while the column was rendered and invisible. It took looking at the
        1440px capture. A test that asserts presence cannot see occlusion.
      */}
      <Table size="small" sx={{ minWidth: 1040 }}>
        <TableHead>
          <TableRow>
            {headers.map((h, i) => (
              <TableCell
                key={h.label}
                align={h.align}
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.62rem",
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  bgcolor: "action.hover",
                  ...(i === headers.length - 1 ? stickyStatusSx : {}),
                }}
              >
                {t(h.label)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {fleet.map((f) => {
            const remaining = sl1Remaining(f)
            const state = sl1ProviderState({
              sl2TenantId: f.sl2TenantId,
              latestJobStatus: f.lastJobStatus,
              latestJobKind: f.lastJobKind,
              imported: f.imported,
              remaining,
            })
            // An unmapped provider has no meaningful "in SL2" figure — there is no
            // SL2 tenant against which anything could have been counted. A zero
            // would assert a measurement nobody made.
            const unmapped = f.sl2TenantId === null
            // The most recent touch of either kind, and which kind it was. An
            // apply that ran after a recon is what the operator wants to see;
            // string compare is safe on ISO instants.
            const lastActivityKind: "recon" | "apply" | undefined =
              f.lastApplyAt && (!f.lastReconAt || f.lastApplyAt > f.lastReconAt)
                ? "apply"
                : f.lastReconAt
                  ? "recon"
                  : undefined
            const lastActivityAt =
              lastActivityKind === "apply" ? f.lastApplyAt : f.lastReconAt
            const covered = unmapped ? 0 : f.matchedInSl2 + f.imported
            const pct = f.sl1Rows > 0 ? (covered / f.sl1Rows) * 100 : 0
            // `covered` mixes an all-time `sl1Rows` with a window-scoped
            // `matchedInSl2` whenever the last recon was windowed, so REMAINING
            // overstates and PROGRESS understates (round-2 F2.2). The direction
            // is the safe one, but REMAINING is the column an operator reads to
            // decide what to run next, so it must not render as a bare fact about
            // the whole history. Both carry the same qualifier IN SL2 does.
            // `sl1WindowScoped`, NOT `!!f.countsWindow`. An all-time recon stores a
            // window with both ends null — present, so truthy — and qualifying its
            // numbers would apologise for a narrowing that did not happen. The two
            // figures are the same measurement in that case.
            const windowScoped = !unmapped && sl1WindowScoped(f.countsWindow)
            const windowTitle = windowScoped
              ? `${t("sl1WindowScopedHint")} ${sl1WindowLabel(f.countsWindow, t)}`
              : undefined
            const stateColor = sl1ProviderStateColor(state)
            const stripeColor =
              stateColor === "success"
                ? "success.main"
                : stateColor === "error"
                  ? "error.main"
                  : stateColor === "info"
                    ? "info.main"
                    : "divider"

            return (
              <TableRow key={f.id} hover>
                <TableCell sx={stripeSx(stripeColor)}>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                    {f.providerName}
                  </Typography>
                  {f.vendor && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.disabled", fontFamily: "monospace" }}
                    >
                      {f.vendor}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <Typography
                      sx={{ fontFamily: "monospace", fontSize: "0.76rem" }}
                    >
                      {f.sl1TenantId}
                    </Typography>
                    <Typography sx={{ color: "text.disabled" }}>→</Typography>
                    {f.sl2TenantId ? (
                      // TRUNCATED, with the full id on hover and in the title
                      // attribute. A real SL2 tenant id is a 26-character ULID —
                      // the mockup drew short slugs (`brunet-lachine`) and a full
                      // ULID here is ~230px, which alone pushed the Status column
                      // off a 1440px screen. The head is enough to tell two
                      // tenants apart at a glance; the whole value is one hover
                      // away and is never needed for typing, only for checking.
                      <Tooltip title={f.sl2TenantId}>
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.76rem",
                            color: "text.secondary",
                            maxWidth: 96,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {f.sl2TenantId}
                        </Typography>
                      </Tooltip>
                    ) : f.sl2TenantAmbiguous ? (
                      // AMBIGUOUS is not the same reading as UNMAPPED, and folding
                      // the two would tell an operator to go and create a mapping
                      // that already exists twice over (`N7`). The row's Status
                      // stays `Needs mapping` — the approved status vocabulary is
                      // six states and this is not a seventh — and the distinction
                      // is carried here as a marker, the same treatment drift gets.
                      <Tooltip
                        title={`${t("sl1AmbiguousTenantHint")} ${(
                          f.sl2TenantCandidates ?? []
                        ).join(", ")}`}
                      >
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <WarningAmberIcon
                            sx={{ fontSize: "0.95rem", color: "warning.main" }}
                          />
                          <Typography
                            sx={{
                              fontSize: "0.76rem",
                              fontStyle: "italic",
                              color: "text.secondary",
                            }}
                          >
                            {t("sl1ColAmbiguousTenant")}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    ) : (
                      // Italic prose, not a dash: an unmapped provider is a real
                      // reading the operator must act on. A blank cell would read
                      // as a rendering fault instead of as work.
                      <Typography
                        sx={{
                          fontSize: "0.76rem",
                          fontStyle: "italic",
                          color: "text.secondary",
                        }}
                      >
                        {t("sl1NoSl2Tenant")}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>

                {/* Drift sits INLINE under the count it qualifies, per the
                    mockup — and per provider, never averaged into the fleet
                    meter: SL1 keeps writing while an import runs, and an
                    aggregate is where that fact goes to die. */}
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {fmtNum(f.sl1Rows)}
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Sl1DriftMarker drift={sl1Drift(f)} />
                  </Box>
                </TableCell>
                {/* The window travels with the number. `sl1Rows` is all-time but
                    a recon only looked at its window, so an unqualified "In SL2"
                    reads as a total it never was. */}
                <TableCell
                  align="right"
                  title={
                    windowScoped ? sl1WindowLabel(f.countsWindow, t) : undefined
                  }
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    color: unmapped ? "text.disabled" : undefined,
                  }}
                >
                  {unmapped ? "—" : fmtNum(f.matchedInSl2)}
                  {windowScoped && (
                    <Typography
                      sx={{
                        display: "block",
                        fontSize: "0.6rem",
                        color: "text.disabled",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("sl1InWindow")}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {fmtNum(f.imported)}
                </TableCell>
                <TableCell
                  align="right"
                  title={windowTitle}
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {fmtNum(remaining)}
                  {windowScoped && (
                    <Typography
                      sx={{
                        display: "block",
                        fontSize: "0.6rem",
                        color: "text.disabled",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("sl1UpperBound")}
                    </Typography>
                  )}
                </TableCell>

                <TableCell sx={{ minWidth: 140 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, pct)}
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "action.hover",
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        minWidth: 32,
                        textAlign: "right",
                      }}
                    >
                      {windowScoped ? "≥" : ""}
                      {Math.round(pct)}%
                    </Typography>
                  </Stack>
                </TableCell>

                {/*
                  The date only, the KIND that produced it, and everything else on
                  hover. The job ids live in the Job history tab, which is one
                  click away and is where an operator goes to open a run.
                */}
                <TableCell
                  title={[
                    f.lastReconAt
                      ? `${t("sl1KindRecon")}: ${fmtStamp(f.lastReconAt).slice(0, 16)}${
                          f.lastReconJobId ? ` (${f.lastReconJobId})` : ""
                        }`
                      : null,
                    f.lastApplyAt
                      ? `${t("sl1KindApply")}: ${fmtStamp(f.lastApplyAt).slice(0, 16)}${
                          f.lastApplyJobId ? ` (${f.lastApplyJobId})` : ""
                        }`
                      : null,
                  ]
                    .filter(Boolean)
                    .join("\n") || undefined}
                  sx={{
                    fontSize: "0.72rem",
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lastActivityAt ? (
                    <>
                      {fmtStamp(lastActivityAt).slice(0, 10)}
                      <Typography
                        sx={{
                          display: "block",
                          fontSize: "0.62rem",
                          color: "text.disabled",
                        }}
                      >
                        {t(
                          lastActivityKind === "apply"
                            ? "sl1KindApply"
                            : "sl1KindRecon",
                        )}
                      </Typography>
                    </>
                  ) : (
                    "—"
                  )}
                </TableCell>
                {/* Pinned — see stickyStatusSx. The action stays on screen at
                    any viewport width. */}
                <TableCell sx={stickyStatusSx}>
                  <Sl1ProviderStateChip state={state} />
                </TableCell>
              </TableRow>
            )
          })}

          {!fleet.length && (
            <TableRow>
              <TableCell colSpan={headers.length} sx={{ textAlign: "center", py: 5 }}>
                <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                  {t("sl1NoFleetYet")}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  )
}

export default Sl1ImportFleetTable
